import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faSync,
	faCheck,
	faPlus,
	faTrash,
} from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../../basics/interface/CustomModal"
import { COLORS } from "../../../utilities/Constants"
import { HTtoTTC, TTCtoHT } from "../../../utilities/Addons"

// Modale de création de commande (V2). Lignes d'articles dynamiques avec
// bascule HT/TTC et bascule prix unitaire / prix total (le backend ne connaît
// que le prix unitaire : en mode total on divise par la quantité à l'envoi).
// Date saisie en natif (YYYY-MM-DD) puis convertie en DD-MM-YYYY pour le
// backend.
const baseLigne = () => ({
	categorie: 0,
	article: 0,
	quantite: 1,
	prixAchat: "",
	prixAchatHT: true,
	prixTotal: false,
})

const toDDMMYYYY = (iso) => {
	if (!iso) return ""
	const [y, m, d] = iso.split("-")
	return `${d}-${m}-${y}`
}

const CommandeModalV2 = ({
	fournisseurs,
	articles,
	categories,
	tva,
	postState,
	onSave,
	onClose,
}) => {
	const [fournisseur, setFournisseur] = useState(0)
	const [datePrevue, setDatePrevue] = useState("") // YYYY-MM-DD
	const [lignes, setLignes] = useState([baseLigne()])

	const busy = postState.saving || postState.saved

	const updateLigne = (index, patch) =>
		setLignes((prev) =>
			prev.map((l, i) => (i === index ? { ...l, ...patch } : l)),
		)

	const changeCategorie = (index, value) =>
		updateLigne(index, { categorie: parseInt(value), article: 0 })

	const toggleHT = (index) => {
		const l = lignes[index]
		const montant = parseFloat(l.prixAchat)
		let nouveau = l.prixAchat
		if (!Number.isNaN(montant)) {
			nouveau = l.prixAchatHT
				? HTtoTTC(montant, tva)
				: TTCtoHT(montant, tva)
		}
		updateLigne(index, { prixAchat: nouveau, prixAchatHT: !l.prixAchatHT })
	}

	// Pas de conversion du montant à la bascule (contrairement au HT/TTC) :
	// on change l'interprétation du chiffre saisi, pas sa valeur — le cas
	// d'usage est « la facture indique un total ».
	const togglePrixTotal = (index) =>
		updateLigne(index, { prixTotal: !lignes[index].prixTotal })

	const addLigne = () => setLignes((prev) => [...prev, baseLigne()])
	const removeLigne = (index) =>
		setLignes((prev) => prev.filter((_, i) => i !== index))

	// En mode prix total la quantité sert de diviseur → elle doit être > 0.
	const ligneInvalide = (l) =>
		l.article === 0 ||
		l.quantite === "" ||
		l.prixAchat === "" ||
		(l.prixTotal && !(parseFloat(l.quantite) > 0))

	const valide =
		datePrevue !== "" &&
		fournisseur > 0 &&
		lignes.length > 0 &&
		!lignes.some(ligneInvalide)

	// Prix unitaire dérivé d'une ligne en mode total (pleine précision float,
	// c'est lui qui part au backend et fait la marge).
	const prixUnitaire = (l) =>
		l.prixTotal
			? parseFloat(l.prixAchat) / parseFloat(l.quantite)
			: l.prixAchat

	const save = () =>
		onSave({
			id: 0,
			fournisseur,
			datePrevue: toDDMMYYYY(datePrevue),
			recu: false,
			restant: 0,
			isHT: false,
			articles: lignes.map((l) => {
				const { prixTotal, ...rest } = l
				return { ...rest, prixAchat: prixUnitaire(l) }
			}),
		})

	return (
		<CustomModal
			title="Nouvelle commande"
			maxWidth={820}
			onClose={onClose}
			showCloseButton={!busy}
			buttons={[
				{
					title: "Annuler",
					styleName: "secondary",
					onClick: onClose,
					disabled: busy,
				},
				{
					title: postState.saving ? (
						<FontAwesomeIcon icon={faSync} spin />
					) : postState.saved ? (
						<FontAwesomeIcon icon={faCheck} />
					) : (
						"Enregistrer"
					),
					styleName: "primary",
					onClick: save,
					disabled: busy || !valide,
				},
			]}
		>
			{/* Fournisseur + date */}
			<div style={s.row2}>
				<div style={s.field}>
					<span style={s.label}>Fournisseur</span>
					<select
						style={s.input}
						value={fournisseur}
						onChange={(e) =>
							setFournisseur(parseInt(e.target.value))
						}
					>
						<option value={0}>-</option>
						{fournisseurs.map((f) => (
							<option key={f.id} value={f.id}>
								{f.nom}
							</option>
						))}
					</select>
				</div>
				<div style={s.field}>
					<span style={s.label}>Date prévue</span>
					<input
						type="date"
						style={s.input}
						value={datePrevue}
						onChange={(e) => setDatePrevue(e.target.value)}
					/>
				</div>
			</div>

			{/* Lignes d'articles */}
			<div style={s.lignesHead}>
				<span style={s.label}>Articles</span>
				<button type="button" style={s.addBtn} onClick={addLigne}>
					<FontAwesomeIcon icon={faPlus} /> Ligne
				</button>
			</div>

			{lignes.map((l, index) => (
				<div key={index} style={s.ligne}>
					<select
						style={{ ...s.input, flex: 1 }}
						value={l.categorie}
						onChange={(e) => changeCategorie(index, e.target.value)}
					>
						<option value={0}>Catégorie</option>
						{categories.map((c) => (
							<option key={c.id} value={c.id}>
								{c.nom}
							</option>
						))}
					</select>
					<select
						style={{ ...s.input, flex: 1 }}
						value={l.article}
						onChange={(e) =>
							updateLigne(index, {
								article: parseInt(e.target.value),
							})
						}
					>
						<option value={0}>Article</option>
						{articles
							.filter((a) => a.categorie === l.categorie)
							.map((a) => (
								<option key={a.id} value={a.id}>
									{a.nom}
								</option>
							))}
					</select>
					<input
						type="number"
						step="any"
						style={{ ...s.input, width: 72, minWidth: 0 }}
						placeholder="Qté"
						value={l.quantite}
						onFocus={(e) => e.target.select()}
						onChange={(e) =>
							updateLigne(index, { quantite: e.target.value })
						}
					/>
					<input
						type="number"
						step="any"
						style={{ ...s.input, width: 92, minWidth: 0 }}
						placeholder={l.prixTotal ? "Prix total" : "Prix/u"}
						title={
							l.prixTotal && !ligneInvalide(l)
								? `= ${prixUnitaire(l).toFixed(4)} / unité`
								: undefined
						}
						value={l.prixAchat}
						onFocus={(e) => e.target.select()}
						onChange={(e) =>
							updateLigne(index, { prixAchat: e.target.value })
						}
					/>
					<button
						type="button"
						style={s.htBtn(!l.prixTotal)}
						onClick={() => togglePrixTotal(index)}
						title="Basculer prix unitaire / prix total de la ligne"
					>
						{l.prixTotal ? "Total" : "P.U."}
					</button>
					<button
						type="button"
						style={s.htBtn(l.prixAchatHT)}
						onClick={() => toggleHT(index)}
						title="Basculer HT / TTC"
					>
						{l.prixAchatHT ? "HT" : "TTC"}
					</button>
					<button
						type="button"
						style={s.delBtn}
						onClick={() => removeLigne(index)}
						disabled={lignes.length === 1}
						title="Supprimer la ligne"
					>
						<FontAwesomeIcon icon={faTrash} />
					</button>
				</div>
			))}
		</CustomModal>
	)
}

const s = {
	row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
	field: { display: "flex", flexDirection: "column", marginBottom: 14 },
	label: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		marginBottom: 6,
	},
	input: {
		height: 38,
		minWidth: 0,
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
	},
	lignesHead: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 8,
		marginTop: 4,
	},
	addBtn: {
		display: "inline-flex",
		alignItems: "center",
		gap: 6,
		height: 30,
		padding: "0 12px",
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 12,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	},
	ligne: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginBottom: 8,
	},
	htBtn: (ht) => ({
		height: 38,
		minWidth: 48,
		borderRadius: 10,
		border: `1px solid ${ht ? COLORS.border : COLORS.accent}`,
		backgroundColor: ht ? COLORS.surface : COLORS.accentLight,
		color: ht ? COLORS.textMuted : COLORS.accent,
		fontSize: 12,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	}),
	delBtn: {
		width: 38,
		height: 38,
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.danger,
		cursor: "pointer",
		fontSize: 13,
		flexShrink: 0,
	},
}

export default CommandeModalV2
